import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import { getFormValues } from 'redux-form';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createFilters, { getFiltersObject } from '../../shared/filters';

import createGeneralForm from '../components/forms/general';
import createLandingsForm from '../components/forms/landings';
import createTargetingForm from '../components/forms/targeting';
import createPromoForm from '../components/forms/promo';
import createIndividualTerms from '../components/forms/individual-terms';
import createRestrictionGeoForm from '../components/forms/restricted-geo';

const mainForm = createGeneralForm({
	namespace: 'programsEdit',
	stateNodeName: 'mainForm'
});

const landingsForm =  createLandingsForm({
	namespace: 'programsEdit',
	stateNodeName: 'landingsForm'
});

const targetingForm = createTargetingForm({
	namespace: 'programsEdit',
	stateNodeName: 'targetingForm'
});

const promoForm = createPromoForm({
	namespace: 'programsEdit',
	stateNodeName: 'promoForm'
});

const restrictionGeoForm = createRestrictionGeoForm({
	namespace: 'programsEdit',
	stateNodeName: 'restrictionGeoForm'
});

const individualTermsForm = createIndividualTerms({
	namespace: 'programsEdit',
	stateNodeName: 'individualTermsForm'
});

const landingsFilters = createFilters({
	namespace: 'programsEditor',
	stateNodeName: 'landingsFilters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'country_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS
			}
		}
	]
});
const individualTermsFilters = createFilters({
	namespace: 'programsEditor',
	stateNodeName: 'individualTermsFilters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'country_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS
			}
		}
	]
});

// actions types
const TOGGLE_PROGRESS_BAR = 'programsEditor/toggle-progress-bar';
const TAB_CHANGE = 'programsEditor/tab-change';

const FETCH_OFFER_SUCCESS = 'programsEditor/fetch-offer-success';
const FETCH_LANDINGS_SUCCESS = 'programEditor/fetch-landings-success';
const FETCH_TARGETING_SUCCESS = 'programEditor/fetch-targeting-success';
const FETCH_INDIVIDUAL_TERMS_SUCCESS = 'programEditor/fetch-individual-terms-success';
const FETCH_LOCATION_RESTRICTIONS_SUCCESS = 'programEditor/fetch-location-restrictions-success';

const FETCH_INDIVIDUAL_TERMS_ERROR = 'programEditor/fetch-individual-terms-error';
const FETCH_ERROR = 'programsEditor/fetch-error';

// permissions
export const allowedPermissions = {
	'view': 'offers_list',
	'edit': 'offers_create',
	'offersChangeCap': 'offers_change_cap',
	'grantOffers': 'offers_grant'
};

// initial state
const initialState = {
	// data its empty array for renderViewState functionality
	// switch view to hasData state
	data: [],
	offer: null,
	landings: null,
	targeting: null,
	individualTerms: null,
	locationRestrictions: null,
	showProgressBar: false,
	defaultLanguage: 'en',
	// available tabs: main, landings
	currentTab: 'main',
	mainForm: mainForm.initialState,
	landingsForm: landingsForm.initialState,
	targetingForm: targetingForm.initialState,
	individualTermsForm: individualTermsForm.initialState,
	promoForm: promoForm.initialState,
	restrictionGeoForm: restrictionGeoForm.initialState,
	landingsFilters: landingsFilters.initialState,
	individualTermsFilters: individualTermsFilters.initialState,
	leadhub_offer_id: '',
	error: false,
	deleteLandingModal: {
		show: false,
		data: {}
	}
};

// actions
export const actions = Object.assign({
	tabChange: tabIndex => (dispatch, getState) => {
		const { offer, currentTab } = getState().programsEditor;

		if (tabIndex === 'main') {
			const { pathname } = window.location;
			const lastParams = parseInt(last(pathname.split('/')));
			if (Number.isInteger(lastParams) && isEmpty(offer)) {
				dispatch(actions.toggleProgressBar());
				dispatch(actions.fetchOfferData(lastParams)).then(action => {
					if (action.type === FETCH_OFFER_SUCCESS) {
						dispatch(actions.fetchTargetingData(action.payload.data.id));
					}
				});
			}
		}

		if (tabIndex === 'landings' && currentTab !== 'landings') {
			dispatch(actions.initLandingsFilters());

			dispatch(actions.toggleProgressBar());
			dispatch(actions.fetchLandingsData(offer.id));
		}

		if (tabIndex === 'targeting' && currentTab !== 'targeting') {
			dispatch(actions.toggleProgressBar());
			dispatch(actions.fetchTargetingData(offer.id));
		}

		if (tabIndex === 'individual-terms' && currentTab !== 'individual-terms') {
			dispatch(actions.initIndividualTermsFilters());

			dispatch(actions.toggleProgressBar());
			dispatch(actions.fetchIndividualTerms(offer.id));
		}

		if (tabIndex === 'restricted-geo' && currentTab !== 'restricted-geo') {
			dispatch(actions.toggleProgressBar());
			dispatch(actions.fetchLocationRestrictions(offer.id));
		}

		dispatch({
			type: TAB_CHANGE,
			payload: tabIndex
		});
	},

	initLandingsFilters: () => (dispatch, getState) => {
		const editorGeoFilter = getState().programsEditor.landingsFilters.selectedFilters
			.find((filter) => filter.name === 'country_ids');
		const listGeoFilter = getState().programsList.filters.selectedFilters
			.find((filter) => filter.name === 'locations_ids');

		if (!editorGeoFilter && listGeoFilter && listGeoFilter.value.length) {
			dispatch(landingsFilters.actions.addFilter('country_ids', false));
			dispatch(landingsFilters.actions.addFilterValue(listGeoFilter.value, 'country_ids'));
		}
	},
	initIndividualTermsFilters: () => (dispatch, getState) => {
		const editorGeoFilter = getState().programsEditor.individualTermsFilters.selectedFilters
			.find((filter) => filter.name === 'country_ids');
		const listGeoFilter = getState().programsList.filters.selectedFilters
			.find((filter) => filter.name === 'locations_ids');

		if (!editorGeoFilter && listGeoFilter && listGeoFilter.value.length) {
			dispatch(individualTermsFilters.actions.addFilter('country_ids', false));
			dispatch(individualTermsFilters.actions.addFilterValue(listGeoFilter.value, 'country_ids'));
		}
	},

	fetchOfferData: id => dispatch => {
		return (http({
			url: `${endpoints.OFFERS.BASE}/${id}`,
			method: 'get',
			actions: [
				FETCH_OFFER_SUCCESS,
				FETCH_ERROR
			]
		}))(dispatch);
	},

	fetchLandingsData: id => (dispatch, getState) => {
		const state = getState().programsEditor;

		const filters = getFiltersObject(state.landingsFilters, false);

		return (http({
			url: endpoints.OFFERS.LANDINGS.replace('{id}', id),
			method: 'get',
			actions: [
				FETCH_LANDINGS_SUCCESS,
				FETCH_ERROR
			],
			query: { filters }
		}))(dispatch);
	},

	fetchLocationRestrictions: id => dispatch => {
		return (http({
			url: endpoints.OFFERS.LOCATION_RESTRICTIONS.replace('{id}', id),
			method: 'get',
			actions: [
				FETCH_LOCATION_RESTRICTIONS_SUCCESS,
				FETCH_ERROR
			]
		}))(dispatch);
	},

	fetchTargetingData: id => dispatch => {
		return (http({
			url: endpoints.OFFERS.TARGETING.replace('{id}', id),
			method: 'get',
			actions: [
				FETCH_TARGETING_SUCCESS,
				FETCH_ERROR
			]
		}))(dispatch);
	},

	fetchIndividualTerms: id => (dispatch, getState) => {
		const state = getState().programsEditor;

		const searchForm = getFormValues('individualTermsSearchForm')(getState());
		const search = searchForm && searchForm.search || '';

		const filters = getFiltersObject(state.individualTermsFilters, false);

		return (http({
			url: endpoints.OFFERS.INDIVIDUAL_TERMS.replace('{id}', id),
			method: 'get',
			actions: [
				FETCH_INDIVIDUAL_TERMS_SUCCESS,
				FETCH_INDIVIDUAL_TERMS_ERROR
			],
			query: { filters, search }
		}))(dispatch);
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	})
},
{
	landingsFiltersActions: landingsFilters.actions,
	individualTermsFiltersActions: individualTermsFilters.actions
},
mainForm.actions,
landingsForm.actions,
targetingForm.actions,
promoForm.actions,
individualTermsForm.actions,
restrictionGeoForm.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => {
		if (action.payload.pathname.indexOf('programs/editor') === -1) {
			return initialState;
		}
		return state;
	},
	[FETCH_OFFER_SUCCESS]: (state, action) => ({
		...state,
		data: action.payload.data,
		offer: action.payload.data,
		showProgressBar: false
	}),
	[FETCH_LANDINGS_SUCCESS]: (state, actions) => ({ ...state, landings: actions.payload.data, showProgressBar: false }),
	[FETCH_LOCATION_RESTRICTIONS_SUCCESS]: (state, actions) => ({
		...state, locationRestrictions: actions.payload.data, showProgressBar: false
	}),
	[FETCH_TARGETING_SUCCESS]: (state, actions) => ({
		...state,
		targeting: actions.payload.data.map(item => {
			item.form_boost_value = Math.round(item.boost_value * 10000) / 100;
			return item;
		}),
		targetingForm: {
			...state.targetingForm,
			activeCheckboxStates: actions.payload.data.map(row => ({
				locationId: row.location_id,
				value: !row.is_active ? (row.affiliate_ids ? 'indeterminate' : 'false') : 'true'
			}))
		},
		showProgressBar: false
	}),
	[FETCH_INDIVIDUAL_TERMS_SUCCESS]: (state, actions) => ({
		...state,
		individualTerms: actions.payload.data,
		showProgressBar: false
	}),
	[FETCH_INDIVIDUAL_TERMS_ERROR]: (state, action) => {
		if (action.payload.response === 403) {
			return { ...state, error: false, showProgressBar: false };
		}
		return { ...state, error: true, showProgressBar: false };
	},
	[FETCH_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[TAB_CHANGE]: (state, action) => ({...state, currentTab: action.payload})
},
mainForm.reducers,
landingsForm.reducers,
targetingForm.reducers,
promoForm.reducers,
individualTermsForm.reducers,
restrictionGeoForm.reducers,
landingsFilters.reducers,
individualTermsFilters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.programsEditor.data,
	getTargeting: state => state.programsEditor.targeting,
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getProgressBar: state => state.programsEditor.showProgressBar,
	getCurrentTab: state => state.programsEditor.currentTab,
	getMainTabLangs: state =>  {
		const { currentLanguage } = state.programsEditor.mainForm;
		return getTabLangs('main', currentLanguage)(state);
	},
	getLandingsTabLangs: state =>  {
		const { currentLanguage } = state.programsEditor.landingsForm;
		return getTabLangs('landings', currentLanguage)(state);
	},
	getRestrictedGeoTabLangs: state => {
		const { currentLanguage } = state.programsEditor.restrictionGeoForm;
		return getTabLangs('restricted-geo', currentLanguage)(state);
	},
	getMainFormData: mainForm.selectors.getMainFormData,
	getLandingsFormData: landingsForm.selectors.getLandingsFormData,
	getTargetingFormData: targetingForm.selectors.getTargetingFormData,
	getActivePayoutCurrencies: targetingForm.selectors.getActivePayoutCurrencies,
	getTargetingActiveCheckboxStates: targetingForm.selectors.getActiveCheckboxStates,
	getIndividualTermsFormData: individualTermsForm.selectors.getIndividualTermsFormData,
	getRestrictedGeoFormData: restrictionGeoForm.selectors.getRestrictedGeoFormData,
	getPromoData: state => ({
		categories: promoForm.selectors.getPromoCategories(state),
		links: promoForm.selectors.getPromoLinks(state),
		isDirty: promoForm.selectors.getIsDirty(state)
	}),
	getPayoutCurrencies: state => state.root.lists['payout-currencies'].map(currency => currency.id),
	getError: state => state.programsEditor.error,
	getAdminData: state => state.root.data,
	getDeleteLandingModal: state => state.programsEditor.deleteLandingModal,
	getLandingsFilters: state => landingsFilters.selectors.getFilters(
		state => state.programsEditor.landingsFilters,
		state => state
	)(state),
	getIndividualTermsFilters: state => individualTermsFilters.selectors.getFilters(
		state => state.programsEditor.individualTermsFilters,
		state => state
	)(state)
};

// function
function getTabLangs(tabIndex, currentLanguage) {
	return state => {
		const { defaultLanguage } = state.programsEditor;
		// maps tabIndex:entity.
		const entityFormLists = { main: 'offer', landings: 'landings', 'restricted-geo': 'locationRestrictions' };
		const entity =  state.programsEditor[entityFormLists[tabIndex]];

		let langs = state.root.lists.languages;

		if (isEmpty(entity)) {
			langs = langs.filter(lang => lang.id === defaultLanguage);
		}

		return langs.map(lang => ({
			...lang,
			isSelected: true,
			isActive: lang.id === currentLanguage,
			isDefault: lang.id === defaultLanguage
		}));
	};
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/editor/index.js